import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import db from "../Firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import Moment from "moment";
import { extendMoment } from "moment-range";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function ViewAgreementPage() {
    const moment = extendMoment(Moment);

    const [certificates, setCertificates] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();
    const tableRef = useRef(null);

    // Auth check
    useEffect(() => {
        const storedUsername = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");

        if (storedRemember === "false" || !storedUsername || !storedPassword) {
            navigate("/signin");
        }
    }, [navigate]);

    // Firestore fetch
    useEffect(() => {
        const q = query(
            collection(db, "jio_bp_certificates"),
            orderBy("timestamp", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCertificates(
                snapshot.docs.map((doc) => ({
                    id: doc.id,
                    data: doc.data(),
                }))
            );
        });

        return () => unsubscribe();
    }, []);

    // Filtering
    const filteredCertificates = certificates.filter(({ data }) => {
        const searchableString = `
      ${data.traineeId || ""}
      ${data.traineeName || ""}
      ${data.certificateNumber || ""}
      ${data.batchId || ""}
      ${data.dateOfIssue || ""}
    `.toLowerCase();

        const matchesSearch = searchableString.includes(
            searchTerm.toLowerCase()
        );

        let issuedDate = null;
        if (data.dateOfIssue) {
            issuedDate = moment(data.dateOfIssue, "DD/MM/YYYY");
        }

        const isAfterStart = startDate
            ? issuedDate
                ? issuedDate.isSameOrAfter(moment(startDate))
                : false
            : true;

        const isBeforeEnd = endDate
            ? issuedDate
                ? issuedDate.isSameOrBefore(moment(endDate))
                : false
            : true;

        return matchesSearch && isAfterStart && isBeforeEnd;
    });

    // Pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredCertificates.slice(
        indexOfFirstRow,
        indexOfLastRow
    );
    const totalPages = Math.ceil(
        filteredCertificates.length / rowsPerPage
    );

    const handleChangePage = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    // Excel Export
    const handleExportExcel = () => {
        const exportData = filteredCertificates.map(({ data }) => ({
            "Trainee Name": data.traineeName,
            "Certificate Number": data.certificateNumber,
            "Trainee Identification No.": data.traineeId,
            "Date of Issue": data.dateOfIssue,
            "Batch ID": data.batchId,
            Timestamp: data.timestamp
                ? moment(
                    data.timestamp.toDate
                        ? data.timestamp.toDate()
                        : data.timestamp
                ).format("DD/MM/YYYY, h:mm A")
                : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Certificates");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `JioBP_Certificates_${moment().format(
                "YYYYMMDD_HHmmss"
            )}.xlsx`
        );
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between mb-3">
                <h5>
                    Certificates Issued (Filtered: {filteredCertificates.length} /
                    Total: {certificates.length})
                </h5>
            </div>

            {/* Filters */}
            <div className="row mb-4 g-2">
                <div className="col-md-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, ID, certificate no, batch..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={startDate}
                        onChange={(e) => {
                            setStartDate(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="col-md-2">
                    <input
                        type="date"
                        className="form-control"
                        value={endDate}
                        onChange={(e) => {
                            setEndDate(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>

                <div className="col-md-1">
                    <button
                        className="btn btn-secondary w-100"
                        onClick={() => {
                            setSearchTerm("");
                            setStartDate("");
                            setEndDate("");
                            setCurrentPage(1);
                        }}
                    >
                        Reset
                    </button>
                </div>

                <div className="col-md-2">
                    <button
                        className="btn btn-success w-100"
                        onClick={handleExportExcel}
                    >
                        Download Excel
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <table
                    className="table table-bordered table-hover"
                    ref={tableRef}
                >
                    <thead className="table-dark">
                        <tr>
                            <th>Sr. No</th>
                            <th>Trainee ID</th>
                            <th>Trainee Name</th>
                            <th>Certificate Number</th>
                            <th>Batch ID</th>
                            <th>Date of Issue</th>
                            <th>Timestamp</th>
                            <th>View</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map(({ id, data }, idx) => (
                            <tr key={id}>
                                <td>{indexOfFirstRow + idx + 1}</td>
                                <td>{data.traineeId}</td>
                                <td>{data.traineeName}</td>
                                <td>{data.certificateNumber}</td>
                                <td>{data.batchId}</td>
                                <td>{data.dateOfIssue}</td>
                                <td>
                                    {data.timestamp
                                        ? moment(
                                            data.timestamp.toDate
                                                ? data.timestamp.toDate()
                                                : data.timestamp
                                        ).format("DD/MM/YYYY, h:mm A")
                                        : "N/A"}
                                </td>
                                <td>
                                    <a
                                        href={`/view_jiobp_certificate.html?id=${id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-info btn-sm text-white"
                                    >
                                        View
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Rows per page:
                    <select
                        className="form-select d-inline-block w-auto ms-2"
                        value={rowsPerPage}
                        onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {[5, 10, 20, 50, 100].map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <button
                        className="btn btn-outline-secondary me-2"
                        onClick={() => handleChangePage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-outline-secondary ms-2"
                        onClick={() => handleChangePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ViewAgreementPage;


// import React, { useState } from "react";
// import { useLsp } from "../store/LspContext";
// import { apiViewAgreement } from "../api/lspApi";

// export default function ViewAgreementPage() {
//     const { state } = useLsp();
//     const [url, setUrl] = useState("");
//     const [out, setOut] = useState("");
//     const [loading, setLoading] = useState(false);

//     const run = async () => {
//         setLoading(true); setOut("");
//         try {
//             const res = await apiViewAgreement(state.baseUrl, state.token, state.partnerLoanId);
//             // response is URL string as per PDF :contentReference[oaicite:16]{index=16}
//             setUrl(typeof res === "string" ? res : "");
//             setOut(JSON.stringify(res, null, 2));
//         } catch (e) {
//             setOut("❌ " + e.message);
//         } finally { setLoading(false); }
//     };

//     return (
//         <div style={card}>
//             <h2 style={h2}>10) View Agreement</h2>
//             <button style={btn} onClick={run} disabled={loading || !state.token || !state.partnerLoanId}>
//                 Get Agreement URL
//             </button>

//             {url && (
//                 <div style={{ marginTop: 10 }}>
//                     <a href={url} target="_blank" rel="noreferrer" style={{ color: "#93c5fd" }}>
//                         Open Agreement
//                     </a>
//                 </div>
//             )}

//             <pre style={pre}>{loading ? "⏳ loading..." : out || "No response yet"}</pre>
//         </div>
//     );
// }
// const card = { padding: 16, border: "1px solid #334155", borderRadius: 12, background: "#0f172a" };
// const h2 = { marginTop: 0 };
// const btn = { padding: "10px 14px", borderRadius: 10, border: "1px solid #475569", background: "#1f2937", color: "#e5e7eb", cursor: "pointer" };
// const pre = { marginTop: 12, background: "#fffff", border: "1px solid #334155", borderRadius: 10, padding: 12, overflow: "auto", maxHeight: 360 };
